import { createRouter, createWebHistory } from 'vue-router';
import HomeView from '../views/HomeView.vue';
import LoginView from '../views/LoginView.vue';
import RegisterView from '../views/RegisterView.vue';
import ProductsView from '../views/ProductsView.vue';
import ProductDetailView from '../views/ProductDetailView.vue';
import ProfileView from '../views/ProfileView.vue';
import EmptyView from '../views/EmptyView.vue';
import ChatView from '../views/ChatView.vue';
import FollowingView from '../views/FollowingView.vue';
import AccountView from '../views/AccountView.vue';
import MyListingsView from '../views/MyListingsView.vue';
import CreateProductView from '../views/CreateProductView.vue';
import EditProductView from '../views/EditProductView.vue';
import RatingsView from '../views/RatingsView.vue';
import ContactView from '../views/ContactView.vue';
import ForgotPasswordView from '../views/ForgotPasswordView.vue';
import EmailVerificationView from '../views/EmailVerificationView.vue';
import ResetPasswordView from '../views/ResetPasswordView.vue';
import DeleteAccountManualView from '../views/DeleteAccountManualView.vue';
import PrivacyPolicyView from '../views/PrivacyPolicyView.vue';
import store from '../store';
import axios from 'axios';

import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';

// Pagination validation guard
const validatePagination = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const page = to.query.page as string;

  if (page) {
    const pageNum = parseInt(page, 10);
    if (isNaN(pageNum) || pageNum < 1) {
      next({
        path: to.path,
        query: { ...to.query, page: '1' },
      });
      return;
    }
  }

  next();
};

// Auth guard funkcija
const requireAuth = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const token = localStorage.getItem('token');
  if (!token) {
    // Sačuvaj željenu putanju da bi se korisnik usmerio nazad nakon prijave
    localStorage.setItem('redirectAfterLogin', to.fullPath);
    next('/login');
    return;
  }

  // Učitaj podatke o korisniku ako još nisu učitani
  if (!store.getters['auth/currentUser']) {
    try {
      await store.dispatch('auth/fetchCurrentUser');
      next();
    } catch (error) {
      // Proveravamo tip greške i pristupamo response statusu samo ako je to axios greška
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.setItem('redirectAfterLogin', to.fullPath);
        next('/login');
      } else {
        // Za druge vrste grešaka, ipak dozvolimo pristup
        next();
      }
    }
  } else {
    next();
  }
};

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
  },
  {
    path: '/contact',
    name: 'contact',
    component: ContactView,
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicyView,
  },
  {
    path: '/delete-account-manual',
    name: 'DeleteAccountManual',
    component: DeleteAccountManualView,
  },
  {
    path: '/products',
    name: 'products',
    component: ProductsView,
    beforeEnter: validatePagination,
  },
  {
    path: '/product/:id',
    name: 'product-detail',
    component: ProductDetailView,
  },
  {
    path: '/profile/:id',
    name: 'profile',
    component: ProfileView,
    beforeEnter: validatePagination,
  },
  {
    path: '/my-listings',
    name: 'my-listings',
    component: MyListingsView,
    beforeEnter: [requireAuth, validatePagination],
    meta: { requiresAuth: true },
  },
  {
    path: '/create-product',
    name: 'create-product',
    component: CreateProductView,
    beforeEnter: requireAuth,
    meta: { requiresAuth: true },
  },
  {
    path: '/messages',
    name: 'messages',
    component: ChatView,
    beforeEnter: requireAuth,
    meta: { requiresAuth: true },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: EmptyView,
    beforeEnter: requireAuth,
    meta: { requiresAuth: true },
  },
  {
    path: '/ratings',
    name: 'ratings',
    component: RatingsView,
    beforeEnter: requireAuth,
    meta: { requiresAuth: true },
  },
  {
    path: '/following',
    name: 'following',
    component: FollowingView,
    beforeEnter: [requireAuth, validatePagination],
    meta: { requiresAuth: true },
  },
  {
    path: '/account',
    name: 'account',
    component: AccountView,
    beforeEnter: requireAuth,
    meta: { requiresAuth: true },
  },
  {
    path: '/edit-product/:id',
    name: 'edit-product',
    component: EditProductView,
    beforeEnter: requireAuth,
    meta: { requiresAuth: true },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: EmailVerificationView,
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Dodajemo globalnu navigacijsku zaštitu
router.beforeEach(async (to, from, next) => {
  // Ako ruta zahteva autentifikaciju i imamo token, ali nemamo podatke o korisniku
  if (
    to.meta.requiresAuth &&
    localStorage.getItem('token') &&
    !store.getters['auth/currentUser']
  ) {
    try {
      // Pokušaj da učitaš podatke o korisniku
      await store.dispatch('auth/fetchCurrentUser');
      next(); // Nastavi ako je uspešno
    } catch (error) {
      console.error('Greška prilikom učitavanja korisničkih podataka:', error);

      // Ako je greška 401, odjavi korisnika i preusmeri na login
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.setItem('redirectAfterLogin', to.fullPath);
        next('/login');
      } else {
        // Za druge greške, dopusti pristup
        next();
      }
    }
  } else {
    // Ako ne zahteva autentifikaciju ili već imamo podatke o korisniku, nastavi normalno
    next();
  }
});

export default router;
